import { convertTaxPercentageToTaxArray } from "@/utils";
import { generateVariationSlug } from "@/utils/common";
import { pick } from "lodash";

export function finalProductCreationMapper(createProductData: any) {
  const data: Record<string, any> = {};

  if (createProductData?.taxPercentage !== undefined) {
    data.taxPercentageList = convertTaxPercentageToTaxArray(createProductData.taxPercentage);
    createProductData.taxPercentage = undefined;
  }
  
  // ✅ REQUIRED
  if (createProductData?._id) {
    data.productId = createProductData._id;
  } else {
    data.name = createProductData.name;
    const optionalFields = [
      'description',
      'barcode',
      'brandId',
      'mrp',
      'hsnCode',
      'totalTaxPercentage',
      'imageList',
      'categoryId',
      'subCategoryId',
      'size',
      'weight',
      'flavor',
      'color',
      'unitList',
      'taxPercentageList',
    ];
    for (const key of optionalFields) {
      const value = createProductData[key];
      // Include value if it's:
      // - a non-empty string
      // - a non-null object or array with length > 0 or keys
      // - a number or boolean
      if (
        typeof value === 'boolean' ||
        typeof value === 'number' ||
        (typeof value === 'string' && value.trim() !== '') ||
        (Array.isArray(value) && value.length > 0) ||
        (typeof value === 'object' &&
          value !== null &&
          Object.keys(value).length > 0)
      ) {
        data[key] = value;
      }
    }
  }
  const baseVariation = {
    variationId: generateVariationSlug(
      pick(createProductData, ['color', 'flavor', 'size', 'weight']) as any,
    ),
    barcode: createProductData?.barcode,
    productCode: createProductData?.productCode,
    price: {
      mrp: createProductData?.mrp,
      unique: true,
      sellingPrice: createProductData?.sellingPrice,
      purchasePrice: createProductData?.purchasePrice,
    },
    stock: {
      productUnit: createProductData?.stock?.productUnit,
      quantity: createProductData?.stock?.quantity,
      minimumAlertQuantity: createProductData?.stock?.stockMinimumQuantity, // Map to API expected name
    },
    variationInfo: pick(createProductData, [
      'color',
      'flavor',
      'size',
      'weight',
      'imageList',
    ]),
    minimumQuantity: {
      quantity: createProductData?.minimumQuantity?.quantity,
      isMultiple: {enable: false},
    },
    markAsNew: true,
    displayStatus: 'Public',
  };

  // Handle additional variations
  data.variationList = [
    baseVariation,
    ...(createProductData.variationList || []).map((variation: any) => ({
      ...variation,
      stock: {
        productUnit: variation.stock?.productUnit,
        quantity: variation.stock?.quantity,
        minimumAlertQuantity: variation.stock?.stockMinimumQuantity, // Map to API expected name
      },
      minimumQuantity: {
        quantity: variation.minimumQuantity?.quantity,
        isMultiple: variation.minimumQuantity?.isMultiple || {enable: false},
      },
      markAsNew: true,
      displayStatus: 'Public',
    })),
  ];

  return data;
}

type NestedObject = {
  [key: string]: any;
};

export function removeEmptyValues(obj: NestedObject): NestedObject {
  if (typeof obj !== 'object' || obj === null) {
    return obj;
  }

  // Handle arrays
  if (Array.isArray(obj)) {
    return obj
      .map(item => removeEmptyValues(item))
      .filter(item => {
        // Remove empty objects and arrays from arrays
        if (typeof item === 'object' && item !== null) {
          return Object.keys(item).length > 0;
        }
        return item !== undefined && item !== null && item !== '';
      });
  }

  // Handle regular objects
  const result: NestedObject = {};

  for (const [key, value] of Object.entries(obj)) {
    // Skip undefined, null, and empty string values
    if (value === undefined || value === null || value === '') {
      continue;
    }

    // Recursively clean nested objects/arrays
    const cleanedValue = removeEmptyValues(value);

    // Only add to result if the cleaned value is non-empty
    if (typeof cleanedValue === 'object' && cleanedValue !== null) {
      if (Array.isArray(cleanedValue)) {
        if (cleanedValue.length > 0) {
          result[key] = cleanedValue;
        }
      } else if (Object.keys(cleanedValue).length > 0) {
        result[key] = cleanedValue;
      }
    } else {
      result[key] = cleanedValue;
    }
  }

  return result;
}